import { createContext, PureComponent } from 'react'
import * as cn from 'classnames'
import * as debounce from 'lodash/debounce'
import * as styles from 'components/features/pfm/Tooltip/Tooltip.css'
import { StatusType } from 'interfaces'

export type TooltipZoneNodeContextType = HTMLElement

export const TooltipZoneNodeContext = createContext<TooltipZoneNodeContextType>(document.body)

// `danger` is the same as `failure`; just for convenience
export type TooltipTheme = StatusType | 'success' | 'warning' | 'danger' | 'failure' | 'info' | 'white' | 'incorrect'
export type TooltipDirection = 'up' | 'down' | 'upLeft'

export type TooltipProps = {
  light?: boolean
  theme?: TooltipTheme
  wrap?: boolean
  direction?: TooltipDirection
  onClick?: () => void
}

type TooltipState = {
  tooltipOffset: number
}

const PAGE_EDGE_PADDING = 8

class Tooltip extends PureComponent<TooltipProps, TooltipState> {
  static defaultProps: Partial<TooltipProps> = {
    theme: 'member',
    direction: 'up',
  }

  static contextType = TooltipZoneNodeContext

  state = {
    tooltipOffset: 0,
    touchesLeftEdge: false,
  }

  context: TooltipZoneNodeContextType

  private tooltipNode: HTMLDivElement | null = null

  render () {
    const { children, light, wrap, theme, direction, onClick } = this.props
    const { tooltipOffset } = this.state

    const tooltipClasses = cn(styles.tooltip, {
      [styles.light]: light,
      [theme ? styles[theme] : '']: Boolean(theme),
    })

    const tooltipStyle = { transform: `translateX(${ tooltipOffset }px)` }
    const arrowStyle = { transform: `translateX(${ -tooltipOffset }px)` }

    const arrowClassName = cn(styles.arrow, {
      [styles.arrowUp]: direction === 'up',
      [styles.arrowDown]: direction === 'down',
      [styles.arrowUpLeft]: direction === 'upLeft',
    })

    return (
      <div
        ref={ (node) => this.tooltipNode = node }
        className={ tooltipClasses }
        style={ tooltipStyle }
        onClick={ onClick }
      >
        <span className={ cn(styles.tooltipContent, { [styles.wrap]: wrap }) }>{ children }</span>
        <span className={ arrowClassName } style={ arrowStyle } />
      </div>
    )
  }

  componentDidMount () {
    this.detectTooltipEdgeCollision()
    window.addEventListener('resize', this.onResizeDebounced)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.onResizeDebounced)
  }

  // TODO: @pmyagkov optimize the method
  private detectTooltipEdgeCollision = () => {
    if (!this.tooltipNode) {
      return
    }

    const zoneRect = this.context.getBoundingClientRect()
    const elRect = this.tooltipNode.getBoundingClientRect()

    let nextTooltipOffset = 0
    const { tooltipOffset } = this.state

    if (elRect.left < zoneRect.left) {
      nextTooltipOffset = -(elRect.left - zoneRect.left - tooltipOffset - PAGE_EDGE_PADDING)
    } else if (elRect.right > zoneRect.right) {
      nextTooltipOffset = tooltipOffset - (elRect.right - zoneRect.right + PAGE_EDGE_PADDING)
    } else {
      if (tooltipOffset !== 0) {
        if (tooltipOffset > 0) {
          // left zone edge
          const normalLeft = elRect.left - zoneRect.left - tooltipOffset
          if (normalLeft < 0) {
            nextTooltipOffset = -(normalLeft - PAGE_EDGE_PADDING)
          }
        }
        if (tooltipOffset < 0) {
          // right zone edge
          const normalRight = elRect.right - tooltipOffset
          if (normalRight > zoneRect.right) {
            nextTooltipOffset = zoneRect.right - normalRight - PAGE_EDGE_PADDING
          }
        }
      }
    }

    this.setState({ tooltipOffset: nextTooltipOffset })
  }

  private onResize: EventListener = () => {
    this.detectTooltipEdgeCollision()
  }

  private onResizeDebounced = debounce(this.onResize, 100)
}

export default Tooltip
